//
// # HierarchySearchRow
//
// A single row in the flat search results view. Shows the matching instance
// name, its target, and the ancestry path breadcrumb so you can see where
// it lives in the hierarchy.
//
// - Click: select the instance in the viewport (top-level only)
// - Double-click: select and zoom to the instance
// - Right-click: context menu with "Open in New Tab" and "Zoom To"
// - Hover: highlight the instance on the canvas
//

import React, { useCallback, useState } from "react";
import type { schema } from "GenAlphaSchema";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import type { HierarchySearchResult } from "./useHierarchyData";
import {
  HierarchyContextMenu,
  type HierarchyContextMenuState,
} from "./HierarchyContextMenu";

interface HierarchySearchRowProps {
  readonly result: HierarchySearchResult;
  // Currently selected row key (for persistent highlight)
  readonly selectedKey: string | null;
  // Callback to select a row by its key
  readonly onSelect: (key: string) => void;
}

export function HierarchySearchRow({
  result,
  selectedKey,
  onSelect,
}: HierarchySearchRowProps) {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const { name, id, targetIdent, targetDisplayName, rootEntityRef, path } =
    result;

  // Build a unique key from the ancestry path + instance identity
  const rowKey = [...path, `${name}:${id}`].join("/");
  const isSelected = selectedKey === rowKey;

  // For nested instances, all viewport interactions (click, hover, zoom)
  // target the root-level ancestor instance, since that's what's visible
  // in the current editor viewport.

  // Click: select in hierarchy + select the containing top-level instance in viewport
  const handleClick = useCallback(() => {
    onSelect(rowKey);

    if (!rootEntityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    content.deselect();
    content.addToSelection(rootEntityRef as never);
  }, [workspace, rootEntityRef, rowKey, onSelect]);

  // Double-click: select and zoom to the containing top-level instance
  const handleDoubleClick = useCallback(() => {
    if (!rootEntityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    content.deselect();
    content.addToSelection(rootEntityRef as never);
    editor.fit();
  }, [workspace, rootEntityRef]);

  // Hover enter: highlight the containing top-level instance on canvas
  const handleMouseEnter = useCallback(() => {
    if (!rootEntityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const entity = rootEntityRef as { highlight: (b: boolean) => unknown };
    entity.highlight(true);
    editor.requestRedraw();
  }, [workspace, rootEntityRef]);

  // Hover leave: remove highlight, restore selection
  const handleMouseLeave = useCallback(() => {
    if (!rootEntityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const entity = rootEntityRef as { highlight: (b: boolean) => unknown };
    entity.highlight(false);

    // Restore highlight on selected entities
    for (const selected of editor.content.selection) {
      selected.highlight(true);
    }
    editor.requestRedraw();
  }, [workspace, rootEntityRef]);

  // Right-click context menu
  const [contextMenu, setContextMenu] =
    useState<HierarchyContextMenuState | null>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleOpenInNewTab = useCallback(() => {
    if (!targetIdent) return;
    workspace.openWithBreadcrumb(targetIdent, snapshot.activeIdent);
  }, [workspace, targetIdent, snapshot.activeIdent]);

  const handleZoomTo = useCallback(() => {
    if (!rootEntityRef) return;

    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    content.deselect();
    content.addToSelection(rootEntityRef as never);
    editor.fit();
  }, [workspace, rootEntityRef]);

  return (
    <>
      <div
        className={`hierarchy-row ${
          isSelected ? "hierarchy-row-selected" : ""
        } ${rootEntityRef ? "" : "hierarchy-row-readonly"}`}
        style={{ paddingLeft: 8 }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
      >
        <div className="hierarchy-search-result">
          {/* Instance name */}
          <span className="hierarchy-name">{name}</span>

          {/* Target display name */}
          {targetDisplayName && (
            <span className="hierarchy-target">{targetDisplayName}</span>
          )}

          {/* Ancestry breadcrumb */}
          {path.length > 0 && (
            <div className="hierarchy-search-path">{path.join(" › ")}</div>
          )}
        </div>
      </div>

      {/* Right-click context menu */}
      {contextMenu && (
        <HierarchyContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
          onOpenInNewTab={handleOpenInNewTab}
          canOpenInNewTab={targetIdent !== null}
          onZoomTo={handleZoomTo}
          canZoomTo={rootEntityRef !== null}
        />
      )}
    </>
  );
}
